export { ANNOTATION_RECIPIENT_URL } from "@repo/fhir-systems/extensions";

import { ANNOTATION_RECIPIENT_URL } from "@repo/fhir-systems/extensions";
// @repo/mentions — the annotation-recipient wire format. One home for the
// mention extension carried on Task-note annotations. Actor-agnostic: a
// Device ref and a Practitioner ref are both just addresses.
//
// Wire shape: annotation.extension = [{ url: ANNOTATION_RECIPIENT_URL,
// valueReference: { reference, display? } }, ...]

import type { Annotation, Extension } from "@medplum/fhirtypes";

/** Extension URL for a mention recorded on a Task-note annotation. */

/** A mentioned recipient — an opaque reference with an optional display. */
export interface MentionedRef {
  reference: string;
  display?: string;
}

function isRecipientExtension(extension: Extension): boolean {
  return extension.url === ANNOTATION_RECIPIENT_URL && extension.valueReference?.reference !== undefined;
}

function toMentionedRef(extension: Extension): MentionedRef {
  const valueReference = extension.valueReference;
  const reference = valueReference?.reference as string;
  const display = valueReference?.display;
  return display !== undefined ? { reference, display } : { reference };
}

/**
 * All refs mentioned on a note's annotation, optionally filtered by a
 * reference prefix (e.g. "Practitioner/") or a full ref (e.g. "Device/C").
 * Never throws on missing/empty/unrelated extensions — returns an empty list.
 */
export function mentionedRefs(annotation: Annotation, filter?: string): MentionedRef[] {
  const refs = (annotation.extension ?? []).filter(isRecipientExtension).map(toMentionedRef);
  if (filter === undefined) return refs;
  return filter.endsWith("/")
    ? refs.filter((r) => r.reference.startsWith(filter))
    : refs.filter((r) => r.reference === filter);
}

/**
 * Encodes a recipient list onto an annotation, replacing any existing
 * recipient extensions. An empty recipient list leaves NO recipient
 * extension on the annotation.
 */
export function withMentions(annotation: Annotation, recipients: MentionedRef[]): Annotation {
  const otherExtensions = (annotation.extension ?? []).filter((e) => !isRecipientExtension(e));
  const recipientExtensions: Extension[] = recipients.map((r) => ({
    url: ANNOTATION_RECIPIENT_URL,
    valueReference:
      r.display !== undefined ? { reference: r.reference, display: r.display } : { reference: r.reference },
  }));
  const extension = [...otherExtensions, ...recipientExtensions];
  if (extension.length === 0) {
    const { extension: _omit, ...rest } = annotation;
    return rest;
  }
  return { ...annotation, extension };
}
